"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { FilterOptionsApiResponse } from "@/types/api";
import type { UrlFilterState } from "./use-url-filters";
import { apiFetch, buildQueryString, ApiClientError, type QueryParamValue } from "@/lib/api/api-client";

export interface UseFilterOptionsResult {
  options: FilterOptionsApiResponse | null;
  /** Table item count — stays stable across filter changes. */
  totalCount: number | null;
  /**
   * Count for the current filter set. Updates immediately when filters change:
   * cleared → total; applied → null (loading) until the server responds.
   */
  matchingCount: number | null;
  matchingCountIsPartial: boolean;
  isLoading: boolean;
  isCountsLoading: boolean;
  error: ApiClientError | null;
  ensureOptionsLoaded: () => void;
}

const EMPTY_OPTIONS: FilterOptionsApiResponse = {
  users: [],
  routes: [],
  methods: [],
  statusCodes: [],
  errorStatuses: [],
  hasMoreUsers: false,
  hasMoreRoutes: false,
  totalCount: 0,
  matchingCount: 0,
  matchingCountIsPartial: false,
};

function filtersQueryKey(filters: UrlFilterState): string {
  return JSON.stringify({
    userIds: filters.userIds,
    routes: filters.routes,
    methods: filters.methods,
    errorStatuses: filters.errorStatuses,
    statusCodes: filters.statusCodes,
    responseStored: filters.responseStored,
    fixedStatus: filters.fixedStatus,
    createdFrom: filters.createdFrom?.toISOString(),
    createdTo: filters.createdTo?.toISOString(),
    updatedFrom: filters.updatedFrom?.toISOString(),
    updatedTo: filters.updatedTo?.toISOString(),
    responseTimeMinMs: filters.responseTimeMinMs,
    responseTimeMaxMs: filters.responseTimeMaxMs,
    search: filters.search,
    routeGroupingEnabled: filters.routeGroupingEnabled,
  });
}

function isUnfiltered(filters: UrlFilterState): boolean {
  return !(
    filters.userIds?.length ||
    filters.routes?.length ||
    filters.methods?.length ||
    filters.errorStatuses?.length ||
    filters.statusCodes?.length ||
    (filters.responseStored && filters.responseStored !== "all") ||
    (filters.fixedStatus && filters.fixedStatus !== "all") ||
    filters.createdFrom ||
    filters.createdTo ||
    filters.updatedFrom ||
    filters.updatedTo ||
    filters.responseTimeMinMs !== undefined ||
    filters.responseTimeMaxMs !== undefined ||
    filters.search
  );
}

function buildFilterParams(
  filters: UrlFilterState,
  extras: { mode?: "full" | "counts" } = {},
): Record<string, QueryParamValue> {
  return {
    mode: extras.mode ?? "full",
    userId: filters.userIds,
    route: filters.routes,
    method: filters.methods,
    status: filters.errorStatuses as string[] | undefined,
    statusCode: filters.statusCodes,
    responseStored: filters.responseStored,
    fixedStatus: filters.fixedStatus,
    createdFrom: filters.createdFrom?.toISOString(),
    createdTo: filters.createdTo?.toISOString(),
    updatedFrom: filters.updatedFrom?.toISOString(),
    updatedTo: filters.updatedTo?.toISOString(),
    responseTimeMinMs: filters.responseTimeMinMs,
    responseTimeMaxMs: filters.responseTimeMaxMs,
    search: filters.search,
    // Route grouping is always on in the UI — keep API facets aligned.
    routeGrouping: "true",
  };
}

/**
 * Loads facet options + counts for the *committed* filter set.
 * Dropdown search is local-only in the UI — do not pass userSearch/routeSearch here.
 */
export function useFilterOptions(filters: UrlFilterState): UseFilterOptionsResult {
  const [options, setOptions] = useState<FilterOptionsApiResponse | null>(null);
  const [liveTotalCount, setLiveTotalCount] = useState<number | null>(null);
  const [liveMatchingCount, setLiveMatchingCount] = useState<number | null>(null);
  const [liveMatchingPartial, setLiveMatchingPartial] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [countsPending, setCountsPending] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const baselineRef = useRef<FilterOptionsApiResponse | null>(null);
  const prevActiveKeyRef = useRef<string | null>(null);

  const activeKey = useMemo(() => filtersQueryKey(filters), [filters]);
  const unfiltered = isUnfiltered(filters);

  // Fast path: dropdown options for the applied filter set.
  useEffect(() => {
    let cancelled = false;
    const filtersChanged = prevActiveKeyRef.current !== activeKey;
    prevActiveKeyRef.current = activeKey;

    setIsLoading(true);
    if (filtersChanged) {
      setCountsPending(true);
      setLiveMatchingCount(null);
    }
    setError(null);

    apiFetch<FilterOptionsApiResponse>(
      `/api/filter-options${buildQueryString(buildFilterParams(filters, { mode: "full" }))}`,
    )
      .then((response) => {
        if (cancelled) return;
        if (isUnfiltered(filters)) baselineRef.current = response;
        setOptions(response);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiClientError ? err : new ApiClientError(500, "INTERNAL_ERROR", "Request failed"));
        if (baselineRef.current) {
          setOptions(baselineRef.current);
        } else {
          setOptions(EMPTY_OPTIONS);
        }
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  // Slow path: live Total/Showing via mode=counts, polled lightly.
  useEffect(() => {
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const loadCounts = (opts?: { showPending?: boolean }) => {
      // Only spin on filter changes — background refreshes must not flash the UI.
      if (opts?.showPending) setCountsPending(true);
      return apiFetch<FilterOptionsApiResponse>(
        `/api/filter-options${buildQueryString(buildFilterParams(filters, { mode: "counts" }))}`,
      )
        .then((response) => {
          if (cancelled) return;
          setLiveTotalCount(response.totalCount);
          setLiveMatchingCount(response.matchingCount);
          setLiveMatchingPartial(Boolean(response.matchingCountIsPartial));
          setCountsPending(false);
        })
        .catch(() => {
          if (cancelled) return;
          // Keep prior live counts; fall back to options totals in the getter below.
          setCountsPending(false);
        });
    };

    void loadCounts({ showPending: true });
    intervalId = setInterval(() => {
      void loadCounts({ showPending: false });
    }, 45_000);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  const totalCount =
    liveTotalCount ?? options?.totalCount ?? baselineRef.current?.totalCount ?? null;

  // While a counts request is in flight for an active filter set, keep matching
  // null so the UI shows a spinner instead of a stale/partial "0+".
  // Unfiltered: surface the fast table total immediately (live recount can lag).
  const matchingCount = countsPending
    ? unfiltered
      ? totalCount
      : null
    : (liveMatchingCount ?? options?.matchingCount ?? null);

  // Unfiltered fast totals are exact enough for page math; only filtered counts
  // stay "partial" until mode=counts returns.
  const matchingCountIsPartial = unfiltered
    ? false
    : countsPending
      ? true
      : liveMatchingCount !== null
        ? liveMatchingPartial
        : Boolean(options?.matchingCountIsPartial);

  // Spin the Showing/After-filters row only when a filter is active and we do
  // not yet have a usable matching total — never block the main page on the
  // slow live table recount.
  const isCountsLoading = !unfiltered && (countsPending || matchingCount === null);

  return {
    options,
    totalCount,
    matchingCount,
    matchingCountIsPartial,
    isLoading,
    isCountsLoading,
    error,
    ensureOptionsLoaded: () => undefined,
  };
}
